import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { DriverDriversRes } from '@/types/driver-drivers';
import { ServiceType } from '@/types/serviceType';

export type RedirectResult = {
  success: boolean;
  redirectPath?: string;
  error?: string;
};

export const useAuthRedirect = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const determineRedirectPath = useCallback((serviceType: ServiceType | undefined): string => {
    if (!serviceType) {
      throw new Error('サービスタイプが定義されていません');
    }

    if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
      return '/vehicle-driver-select';
    }
    if (serviceType === ServiceType.PreBooking) {
      return '/drive-list';
    }

    throw new Error(`サポートされていないサービスタイプ: ${serviceType as string}`);
  }, []);

  const redirectAfterLogin = useCallback(
    (driverData: DriverDriversRes['driverData']): RedirectResult => {
      try {
        const redirectPath = determineRedirectPath(driverData.serviceType);

        return {
          success: true,
          redirectPath,
        };
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : t('login.unexpectedError');

        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [determineRedirectPath, t],
  );

  const navigateToPath = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  return {
    determineRedirectPath,
    redirectAfterLogin,
    navigateToPath,
  };
};
